import React from 'react';
import { Typography } from '@mui/material';

import { styles } from './styles';

export const getContentComponent = (type) => definitionDataComponent[type];

export const getChartType = (param) => {
	if (!param) {
		return '';
	}
	return param?.replace(/\s/g, '-').toLocaleLowerCase();
};

const definitionDataComponent = {
	anomaly: {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>What is an anomaly?</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					An anomalous dept/catg/subcatg is one where the change in supplier
					dollar share is more/less than expected, and this change is
					statistically significant. The expected behavior is benchmarked using
					the reference window, and the selected window performance is compared
					against it
				</Typography>
			),
		},
		performaing: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Outperforming, underperforming and in-line
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					<Typography>
						Outperforming: If supplier dollar share of the dept/catg/subcatg is
						tagged as an anomaly and has gone up as compared to the reference
						window
					</Typography>
					<Typography>
						Underperforming: If supplier dollar share of the dept/catg/subcatg
						is tagged as an anomaly and has gone down as compared to the
						reference window
					</Typography>
					<Typography>
						Inline: If supplier dollar share of the dept/catg/subcatg is not
						tagged as an anomaly
					</Typography>
				</Typography>
			),
		},
		logic: {
			title: (
				<Typography sx={{ ...styles.title }}>How it is calculated ?</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Supplier dollar share is calculated in the reference and selected
					window and are then compared through a statistical test to check if
					the difference in supplier dollar share is significant or not. In
					case, the supplier dollar share comes out to be significantly
					different from the reference window based on the statistical test, it
					is then tagged as an anomaly
				</Typography>
			),
		},
		window: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Time window for calculation
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					<Typography>
						Selected window: Weeks selected by the supplier (Minimum 4 weeks are
						required for DS insights)
					</Typography>
					<Typography>
						Reference Window: Same number of weeks, for comparison, prior to the
						selected window
					</Typography>
				</Typography>
			),
		},
	},
	'net-sales': {
		metric: {
			title: <Typography sx={{ ...styles.title }}>Net Sales</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Revenue as recorded financially (may also see net sales labeled as
					revenue in reporting). For all sales except marketplace (MP) and
					services, net sales = GMV. However, for MP and services sales, net
					sales is commission.
				</Typography>
			),
		},
		logic: {
			title: (
				<Typography sx={{ ...styles.title }}>Aggregation Logic</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Calculated at an item level and aggregated as a sum across the
					following levels:
					<ol>
						<li>Item hierarchy: Category, Subcategory, Brand</li>
						<li>Location hierarchy: Region, Subdivision, Store</li>
						<li>Selected Time range</li>
					</ol>
					YoY % calculation = (TY sales number - LY sales number) / LY sales
					number * 100
				</Typography>
			),
		},
	},
	units: {
		metric: {
			title: <Typography sx={{ ...styles.title }}>Units</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Total number of units sold to customers for transactions online and in
					store.
				</Typography>
			),
		},
		logic: {
			title: (
				<Typography sx={{ ...styles.title }}>Aggregation Logic</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Calculated at an item level and aggregated as a sum across the
					following levels:
					<ol>
						<li>Item hierarchy: Category, Subcategory, Brand</li>
						<li>Location hierarchy: Region, Subdivision, Store </li>
						<li>Selected Time range</li>
					</ol>
					YoY % calculation = (TY Units number - LY Units number) / LY Units
					number * 100
				</Typography>
			),
		},
	},
	gmv: {
		metric: {
			title: <Typography sx={{ ...styles.title }}>GMV</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					GMV or Gross Merchandise Value is the revenue collected from the
					customer, or item price after savings, discounts, and shipping
					charges. For all sales except marketplace (MP) and services, net sales
					= GMV. However, for MP and services sales, net sales is commission.
				</Typography>
			),
		},
		logic: {
			title: (
				<Typography sx={{ ...styles.title }}>Aggregation Logic</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Calculated at an item level and aggregated as a sum across the
					following levels.
					<ol>
						<li>Item hierarchy: Category, Subcategory, Brand </li>
						<li>Location hierarchy: Region, Subdivision, Store </li>
						<li>Selected Time range.</li>
					</ol>
					Note that &apos;GMV&apos; metric is not available for
					&apos;Location&apos; hierarchy.
					<br />
					YoY % calculation = (TY GMV number - LY GMV number) / LY GMV number *
					100
				</Typography>
			),
		},
	},
	'on-hand': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>On-Hand Inventory</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Total inventory across Store channel
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Daily Average inventory
				</Typography>
			),
		},
		logic: {
			title: (
				<Typography sx={{ ...styles.title }}>Aggregation Logic</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Calculated at an item level and aggregated as a daily average across
					the following levels:
					<ol>
						<li>Item hierarchy: Category, Subcategory, Brand </li>
						<li>Location hierarchy: Region, Subdivision, Store</li>
						<li>Selected Time Range: </li>
						&#8226; If a timeframe is selected that includes yesterday as the
						end-date (WMT Week to Date for example), Daily Average Inventory =
						yesterday’s inventory value.
						<br />
						&#8226; If entire WM weeks is selected (Sat-Fri; WMT Last 4 wks or
						WMT last 52 wks for example), average of daily inventory of those
						specific dates is used for calculation
					</ol>
				</Typography>
			),
		},
	},
	'repl-instock-%': {
		metric: {
			title: <Typography sx={{ ...styles.title }}>Repl Instock %</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					A measure of the ability of stores to meet the current day’s demand
					forecast.
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Percent of stores that have at least one item on hand. On Hand Store
					Count / Valid Store Count * 100. On Hand store count is how many
					stores had at least one item on hand. Valid Store Count is how many
					stores are valid (replenishable) for that item.
				</Typography>
			),
		},
		logic: {
			title: (
				<Typography sx={{ ...styles.title }}>Aggregation Logic</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Calculated at an item level and aggregated as a daily average across
					the following levels:
					<ol>
						<li>Item hierarchy: Category, Subcategory, Brand </li>
						<li>Location hierarchy: Region, Subdivision, Store </li>
						<li>Selected Time Range: </li>
						&#8226; If a timeframe is selected that includes yesterday as the
						end-date (WMT Week to Date for example), Daily Average Inventory =
						yesterday’s inventory value.
						<br />
						&#8226; If entire WM weeks is selected (Sat-Fri; WMT Last 4 wks or
						WMT last 52 wks for example), average of daily inventory of those
						specific dates is used for calculation
					</ol>
				</Typography>
			),
		},
	},
	'in-transit': {
		metricsAndFormulae: [
			{
				metric: {
					title: (
						<Typography sx={{ ...styles.inTransitTitle }}>
							In transit Inventory - On Order
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.inTransitContent }}>
							Inventory that has been ordered by stores(as of previous day)
						</Typography>
					),
				},
				formula: {
					title: (
						<Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>
					),
					content: (
						<Typography sx={{ ...styles.inTransitContent }}>
							Sum (On-Order quantity) Number of items (eaches) currently on
							order by the store
						</Typography>
					),
				},
			},
			{
				metric: {
					title: (
						<Typography sx={{ ...styles.inTransitTitle }}>
							In transit Inventory - DC to Store
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.inTransitContent }}>
							DC to Store metric represents the store order for merchandise that
							has left the Distribution Center, but has not yet arrived at the
							store, as of the previous day
						</Typography>
					),
				},
			},
			{
				metric: {
					title: (
						<Typography sx={{ ...styles.inTransitTitle }}>
							In transit Inventory - In Warehouse
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.inTransitContent }}>
							In-Warehouse metrics represents the items currently in the
							warehouse pulled for a store but not shipped yet (as of the
							previous day)
						</Typography>
					),
				},
				formula: {
					title: (
						<Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>
					),
					content: (
						<Typography sx={{ ...styles.inTransitContent }}>
							In-warehouse retail = In-warehouse quantity(Item) * Retail
							Amount(Item)
						</Typography>
					),
				},
			},
		],
		logic: {
			title: (
				<Typography sx={{ ...styles.inTransitTitle }}>
					Aggregation Logic
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					Calculated at an item level and aggregated as a daily average across
					the following levels:
					<ol>
						<li>Item hierarchy: Category, Subcategory, Brand</li>
						<li>Location hierarchy: Region, Subdivision, Store </li>
						<li>Selected Time Range: </li>
						&#8226; If a timeframe is selected that includes yesterday as the
						end-date (WMT Week to Date for example), Daily Average Inventory =
						yesterday’s inventory value.
						<br />
						&#8226; If entire WM weeks is selected (Sat-Fri; WMT Last 4 wks or
						WMT last 52 wks for example), average of daily inventory of those
						specific dates is used for calculation
					</ol>
				</Typography>
			),
		},
	},
	'channel-breakdown': {
		store: {
			title: <Typography sx={{ ...styles.cbTitle }}>Store</Typography>,
			content: [
				{
					title: (
						<Typography sx={{ ...styles.cbSubTitle }}>
							Buy in Store (BIS)
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.cbSubContent }}>
							Purchased in the stores through store register.
						</Typography>
					),
				},
				{
					title: (
						<Typography sx={{ ...styles.cbSubTitle }}>
							Delivery (DLV)
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.cbSubContent }}>
							Order placed online to be delivered to the customer from the
							store. It includes Scheduled and Unscheduled Delivery, In-Home
							Delivery, and Express Delivery.
						</Typography>
					),
				},
				{
					title: (
						<Typography sx={{ ...styles.cbSubTitle }}>Pickup (PU)</Typography>
					),
					content: (
						<Typography sx={{ ...styles.cbSubContent }}>
							Order placed online for the customer to pick up in store.
						</Typography>
					),
				},
				{
					title: (
						<Typography sx={{ ...styles.cbSubTitle }}>
							Ship from Store (SFS)
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.cbSubContent }}>
							Order placed online and shipped to the customer from the store via
							carrier (e.g. FedEx)
						</Typography>
					),
				},
			],
		},
		eComm: {
			title: <Typography sx={{ ...styles.cbTitle }}>eComm</Typography>,
			content: [
				{
					title: (
						<Typography sx={{ ...styles.cbSubTitle }}>
							Ship to Home (S2H)
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.cbSubContent }}>
							Order placed online and shipped from one of the fulfillment
							centers.
						</Typography>
					),
				},
				{
					title: (
						<Typography sx={{ ...styles.cbSubTitle }}>
							Ship to Store (S2S)
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.cbSubContent }}>
							Order placed online and shipped to a store for pick up in store.
						</Typography>
					),
				},
			],
		},
	},
	wos: {
		metricsAndFormulae: [
			{
				metric: {
					title: (
						<Typography sx={{ ...styles.title }}>
							Warehouse on Order Weeks of Supply (WOS)
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.content }}>
							The measure of how many weeks the warehouse on order inventory
							quantity, for a particular item, will last given the current
							average warehouse sales quantity
						</Typography>
					),
				},
				formula: {
					title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
					content: (
						<Typography sx={{ ...styles.content }}>
							Warehouse on Order quantity on last Friday/Average warehouse sales
							quantity for the last 4 weeks
						</Typography>
					),
				},
			},
			{
				metric: {
					title: (
						<Typography sx={{ ...styles.title }}>
							Warehouse on Hand Weeks of Supply (WOS)
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.content }}>
							The a measure of how many weeks the warehouse on hand inventory
							quantity, for a particular item, will last given the current
							average warehouse sales quantity
						</Typography>
					),
				},
				formula: {
					title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
					content: (
						<Typography sx={{ ...styles.content }}>
							Warehouse on Hand quantity on last Friday/Average warehouse sales
							quantity for the last 4 weeks
						</Typography>
					),
				},
			},
			{
				metric: {
					title: (
						<Typography sx={{ ...styles.title }}>
							Store on Hand Weeks of Supply (WOS)
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.content }}>
							The measure of how many weeks the store on hand inventory
							quantity, for a particular item, will last given the current
							average store sales quantity
						</Typography>
					),
				},
				formula: {
					title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
					content: (
						<Typography sx={{ ...styles.content }}>
							Store on Hand quantity on last Friday/Average Store sales quantity
							for the last 4 weeks
						</Typography>
					),
				},
			},
			{
				metric: {
					title: (
						<Typography sx={{ ...styles.title }}>
							Store on Order Weeks of Supply (WOS)
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.content }}>
							The measure of how many weeks the store on order inventory
							quantity, for a particular item, will last given the current
							average store sales quantity
						</Typography>
					),
				},
				formula: {
					title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
					content: (
						<Typography sx={{ ...styles.content }}>
							Store on Order quantity on last Friday/Average Store sales
							quantity for the last 4 weeks
						</Typography>
					),
				},
			},
			{
				metric: {
					title: (
						<Typography sx={{ ...styles.title }}>
							Store in Transit Weeks on Supply (WOS)
						</Typography>
					),
					content: (
						<Typography sx={{ ...styles.content }}>
							The measure of how many weeks the store in transit inventory
							quantity, for a particular item, will last given the forecasted
							average store sales quantity
						</Typography>
					),
				},
				formula: {
					title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
					content: (
						<Typography sx={{ ...styles.content }}>
							Store in Transit quantity on last Friday/Average Store sales
							quantity for the upcoming 4 weeks
						</Typography>
					),
				},
			},
		],
	},
	aur: {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					AUR (Average Unit Revenue){' '}
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Average Unit Revenue (AUR) reflects the average price (excluding
					savings, discounts, or shopping revenues) of the sold items in your
					category or sub-category
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Average Unit Retail (AUR) is GMV divided by Units.
				</Typography>
			),
		},
	},
	'total-inventory': {
		metric: {
			title: <Typography sx={{ ...styles.title }}>Total Inventory</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Store Total Inventory is based upon perpetual inventory (as opposed to
					financial inventory). This includes both inventory physically in the
					stores (store on-hands) along with inventory in the distribution
					centers (DC on-hands). Online Total Inventory is based upon perpetual
					inventory (as opposed to financial inventory). This includes inventory
					in the FC on-hands distribution centers.
				</Typography>
			),
		},
		logic: {
			title: <Typography sx={{ ...styles.title }}></Typography>,
			content: <Typography sx={{ ...styles.content }}></Typography>,
		},
	},
	'buy-in-store-(bis)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Buy in Store (BIS)</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Purchased in the stores through store register.
				</Typography>
			),
		},
	},
	'pick-up-(pu)': {
		metric: {
			title: <Typography sx={{ ...styles.title }}>Pickup(PU)</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Order placed online for the customer to pick up in store.
				</Typography>
			),
		},
	},
	delivery: {
		metric: {
			title: <Typography sx={{ ...styles.title }}>Delivery (DLV)</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Order placed online to be delivered to the customer from the store. It
					includes Scheduled and Unscheduled Delivery, In-Home Delivery, and
					Express Delivery.
				</Typography>
			),
		},
	},
	'ship-from-store-(sfs)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Ship from Store (SFS)</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Order placed online and shipped to the customer from the store via
					carrier (e.g. FedEx)
				</Typography>
			),
		},
	},
	'ship-to-home-(s2h)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Ship to Home (S2H)</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Order placed online and shipped from one of the fulfillment centers.
				</Typography>
			),
		},
	},
	'ship-to-store-(s2s)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Ship to Store (S2S)</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Order placed online and shipped to a store for pick up in store.
				</Typography>
			),
		},
	},
	'nil-pick-rate(%)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Scheduled Nil Pick Rate(%):-
				</Typography>
			),
			content: (
				<>
					<Typography sx={{ ...styles.content }}>
						The percentage of items that were not fulfilled for scheduled
						orders. This is reported in a percent. Calculation: (Scheduled Nil
						Pick Rate Numerator) / (Scheduled Nil Pick Rate Denominator).
					</Typography>
					<Typography sx={{ ...styles.title }}>
						<li>Calculation:</li>
					</Typography>
					<Typography sx={{ ...styles.content }}>
						(Scheduled Nil Pick Rate Numerator) / (Scheduled Nil Pick Rate
						Denominator).
					</Typography>
					<Typography sx={{ ...styles.title }}>
						Unscheduled Nil Pick Rate(%):-
					</Typography>
					<Typography sx={{ ...styles.content }}>
						The measure of percentage of items that were not fulfilled for
						unscheduled orders. This is reported in a percent.
					</Typography>
					<Typography sx={{ ...styles.title }}>
						<li>Calculation:</li>{' '}
					</Typography>
					<Typography sx={{ ...styles.content }}>
						(Unscheduled Nil Pick Rate Numerator) / (Unscheduled Nil Pick Rate
						Denominator).
					</Typography>
				</>
			),
		},
	},
	'first-time-pick-rate(%)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					First Time Pick Rate(%):
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					The percentage of items that were picked in the first pick-walk for
					store fulfilled orders.
				</Typography>
			),
		},
		logic: {
			title: (
				<Typography sx={{ ...styles.title }}>Calculation Logic</Typography>
			),
			content: (
				<>
					<Typography sx={{ ...styles.content }}>
						Calculation: (First Time Pick Rate Numerator) / (First Time Pick
						Rate Denominator)
					</Typography>
					<Typography sx={{ ...styles.content }}>
						<li>-Picks:</li> All picks, as ordered, on the first picking
						attempt. It is based on the first pick display timestamp. It does
						not include any exception picks and items that are cancelled prior
						to dispense.
						<li>-Overrides:</li> The system says that the associate scanned the
						wrong item, when in fact the associate scanned the correct item. The
						manager can override the system to tell it to accept the associates
						pick. When an override is done for the original item, it will not
						lead to a substitution. These are added only if they are accepted
						through the exception management. <li>-Substitution</li>: The system
						recommends a different item for the OPD associate to pick since the
						original item is out or cant be found.
					</Typography>
				</>
			),
		},
	},
	'pre-substitute-rate(%)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Pre Substitute Rate(%)</Typography>
			),
			content: (
				<>
					<Typography sx={{ ...styles.content }}>
						This is an indicator of how often the customer got exactly what they
						ordered before any substitution.
					</Typography>
					<Typography sx={{ ...styles.content }}>
						<li>Calculation:</li> (Pre-Substitute Rate Numerator) /
						(Pre-Substitute Rate Denominator)
					</Typography>
				</>
			),
		},
	},
	'post-substitute-rate(%)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Post Substitute Rate(%):
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					An indicator of how often the customer got an item for their order
					including substitutions and manager approved overrides. Calculation:
					(Post Substitute Rate Numerator) / (Post Substitute Rate Denominator).
				</Typography>
			),
		},
	},
	'on-hand-inventory': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>On-Hand Inventory</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Total inventory across Store channel
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Daily Average inventory
				</Typography>
			),
		},
		logic: {
			title: (
				<Typography sx={{ ...styles.title }}>Aggregation Logic</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Calculated at an item level and aggregated as a daily average across
					the following levels:
					<ol>
						<li>Item hierarchy: Category, Subcategory, Brand </li>
						<li>Location hierarchy: Region, Subdivision, Store</li>
						<li>Selected Time Range: </li>
						&#8226; If a timeframe is selected that includes yesterday as the
						end-date (WMT Week to Date for example), Daily Average Inventory =
						yesterday’s inventory value.
						<br />
						&#8226; If entire WM weeks is selected (Sat-Fri; WMT Last 4 wks or
						WMT last 52 wks for example), average of daily inventory of those
						specific dates is used for calculation
					</ol>
				</Typography>
			),
		},
	},
	'net-sales---stores': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Net Sales - Stores</Typography>
			),
			content: <Typography sx={{ ...styles.content }}>TBD</Typography>,
		},
	},
	'net-sales-by-channel': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Net Sales - Stores</Typography>
			),
			content: <Typography sx={{ ...styles.content }}>TBD</Typography>,
		},
	},
	'inventory-trends': {
		metric: {
			title: <Typography sx={{ ...styles.title }}>Inventory Trends</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Sales performance and inventory trends over a specified time period.
				</Typography>
			),
		},
	},
	'on-order': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					In transit Inventory - On Order{' '}
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Inventory that has been ordered by stores(as of previous day)
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					Sum (On-Order quantity) Number of items (eaches) currently on order by
					the store
				</Typography>
			),
		},
	},

	'dc-to-store': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					In transit Inventory - DC to Store{' '}
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					DC to Store metric represents the store order for merchandise that has
					left the Distribution Center, but has not yet arrived at the store, as
					of the previous day
				</Typography>
			),
		},
	},
	'in-whse': {
		metric: {
			title: <Typography sx={{ ...styles.title }}>In WHSE </Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					n-Warehouse metrics represents the items currently in the warehouse
					pulled for a store but not shipped yet (as of the previous day)
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					In-warehouse retail = In-warehouse quantity(Item) * Retail
					Amount(Item)
				</Typography>
			),
		},
		logic: {
			title: (
				<Typography sx={{ ...styles.inTransitTitle }}>
					Aggregation Logic
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					Calculated at an item level and aggregated as a daily average across
					the following levels:
					<ol>
						<li>Item hierarchy: Category, Subcategory, Brand</li>
						<li>Location hierarchy: Region, Subdivision, Store </li>
						<li>Selected Time Range: </li>
						&#8226; If a timeframe is selected that includes yesterday as the
						end-date (WMT Week to Date for example), Daily Average Inventory =
						yesterday’s inventory value.
						<br />
						&#8226; If entire WM weeks is selected (Sat-Fri; WMT Last 4 wks or
						WMT last 52 wks for example), average of daily inventory of those
						specific dates is used for calculation
					</ol>
				</Typography>
			),
		},
	},
	'traited-store-count': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Traited Store Count </Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Store count of all the items with the replenishment status as Active,
					Inactive and New.
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>Sum(Traited Stores)</Typography>
			),
		},
	},
	'traited-item-count': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Traited Item Count</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Item count that are replenishable
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>Sum(Traited Items)</Typography>
			),
		},
	},
	'valid-store-count': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Valid Store Count</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Valid Store count of all the items with the replenishment status as
					Active
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Sum of Valid Stores i.e. Sum(Stores)
				</Typography>
			),
		},
	},
	'store-on-hand-quantity': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Store On Hand Quantity</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					The quantity of an item which is on-hand at this store.
				</Typography>
			),
		},
		content: (
			<Typography sx={{ ...styles.content }}>
				Daily Average inventory
			</Typography>
		),
		formula: {
			title: <Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					Sum of On hand Quantity divided by no of days.
				</Typography>
			),
		},
	},
	'store-on-order-quantity': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Store On Order Quantity
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					The number of units on a purchase order allocated specifically to the
					store.
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					Sum (On Order quantity)/ Number of days
				</Typography>
			),
		},
	},
	'store-in-transit-quantity': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Store In Transit Quantity
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					The number of units invoiced by DC to the store but not yet received
					and finalized.
				</Typography>
			),
			formula: {
				title: (
					<Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>
				),
				content: (
					<Typography sx={{ ...styles.inTransitContent }}>
						Sum (On-Transit quantity) Number of days
					</Typography>
				),
			},
		},
	},
	'store-in-warehouse-quantity': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Store In Warehouse Quantity
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					The number of units received by the DC which are allocated
					specifically to a store but not yet shipped (invoiced).
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					In-warehouse retail = In-warehouse quantity(Item) * Retail
					Amount(Item)
				</Typography>
			),
		},
	},
	'repl-instock-%-(store)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Repl Instock % (Store)</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					It is a measurement on the percentage of the inventory that is getting
					replenished that becomes in stock inventory
				</Typography>
			),
		},
	},
	'total-order-quantity': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Total Order Quantity</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					The total number of units ordered by the customer and received by the
					store.
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.content }}>
					Sum(Quantity of Items ordered by the customer)
				</Typography>
			),
		},
	},
	'pre-substitute-rate': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Pre Substitute Rate(%)</Typography>
			),
			content: (
				<>
					<Typography sx={{ ...styles.content }}>
						This is an indicator of how often the customer got exactly what they
						ordered before any substitution.
					</Typography>
					<Typography sx={{ ...styles.content }}>
						<li>Calculation:</li> (Pre-Substitute Rate Numerator) /
						(Pre-Substitute Rate Denominator)
					</Typography>
				</>
			),
		},
	},
	'post-subtitute-rate': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Post Substitute Rate(%):
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					An indicator of how often the customer got an item for their order
					including substitutions and manager approved overrides.{' '}
					<li>Calculation:</li>
					(Post Substitute Rate Numerator) / (Post Substitute Rate Denominator).
				</Typography>
			),
		},
	},
	'nil-pick': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Scheduled Nil Pick Rate(%):-
				</Typography>
			),
			content: (
				<>
					<Typography sx={{ ...styles.content }}>
						The percentage of items that were not fulfilled for scheduled
						orders. This is reported in a percent. Calculation: (Scheduled Nil
						Pick Rate Numerator) / (Scheduled Nil Pick Rate Denominator).
					</Typography>
					<Typography sx={{ ...styles.title }}>
						<li>Calculation:</li>
					</Typography>
					<Typography sx={{ ...styles.content }}>
						(Scheduled Nil Pick Rate Numerator) / (Scheduled Nil Pick Rate
						Denominator).
					</Typography>
					<Typography sx={{ ...styles.title }}>
						Unscheduled Nil Pick Rate(%):-
					</Typography>
					<Typography sx={{ ...styles.content }}>
						The measure of percentage of items that were not fulfilled for
						unscheduled orders. This is reported in a percent.
					</Typography>
					<Typography sx={{ ...styles.title }}>
						<li>Calculation:</li>{' '}
					</Typography>
					<Typography sx={{ ...styles.content }}>
						(Unscheduled Nil Pick Rate Numerator) / (Unscheduled Nil Pick Rate
						Denominator).
					</Typography>
				</>
			),
		},
	},
	'dollar/store/week(day)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Dollar/store/week(day)</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					The average of the total net sales, per distinct valid store, over the
					number of weeks during the selected Walmart week for the included
					stores.
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					Total net sales/number of stores/number of weeks • For the Total net
					sales, it is POS sales, which is pos_sales_this_year in
					chnl_perf_store_fact_dly • For the number of stores, it is valid store
					count, in the table ww_chnl_perf_app.chnl_perf_store_sku_dly, do
					ty_repl_ind = 1, enter the right bus_dt and wm_item_nbr, to get the
					corresponding store_nbr and only do distinct count valid store. • For
					the number of weeks or days, that’s based on the selection user made
					in report builder calendar. Eg if they select last 4 weeks, then it
					will be 4.
				</Typography>
			),
		},
	},
	'units/store/week(day)': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Units/store/week(day)</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					The average of the number of units for the total net sales, per
					distinct valid store, over the number of weeks during the selected
					Walmart for the included stores.{' '}
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					Units of Total net sales/number of stores/number of weeks
				</Typography>
			),
		},
	},
	'pre-substitute-quantity': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Pre-Substitute Quantity
				</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Measures the sum of items picked as ordered and manager approved
					overrides.
				</Typography>
			),
		},
	},
	'substitute-quantity': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Substitute Quantity</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					Defines a similar item that is picked when the original ordered item
					is not available or is not found in a store
				</Typography>
			),
		},
	},
	'nil-pick-quantity': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Nil Pick Quantity</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					This is a measure of the of units not picked or substituted by a
					store. In other words, Quantity Not Picked
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					Sum(Quantity of the Items not picked)
				</Typography>
			),
		},
	},
	'first-time-pick-rate': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>First Time Pick Rate</Typography>
			),
			content: (
				<Typography sx={{ ...styles.content }}>
					The calculation that identifies the percentage of items that were
					picked as ordered the first time
				</Typography>
			),
		},
		formula: {
			title: <Typography sx={{ ...styles.inTransitTitle }}>Formula</Typography>,
			content: (
				<Typography sx={{ ...styles.inTransitContent }}>
					Sum(Quantity of the Items picked up the first time)
				</Typography>
			),
		},
	},
	'nil-pick-rate': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>
					Scheduled Nil Pick Rate(%):-
				</Typography>
			),
			content: (
				<>
					<Typography sx={{ ...styles.content }}>
						The percentage of items that were not fulfilled for scheduled
						orders. This is reported in a percent. Calculation: (Scheduled Nil
						Pick Rate Numerator) / (Scheduled Nil Pick Rate Denominator).
					</Typography>
					<Typography sx={{ ...styles.title }}>
						<li>Calculation:</li>
					</Typography>
					<Typography sx={{ ...styles.content }}>
						(Scheduled Nil Pick Rate Numerator) / (Scheduled Nil Pick Rate
						Denominator).
					</Typography>
					<Typography sx={{ ...styles.title }}>
						Unscheduled Nil Pick Rate(%):-
					</Typography>
					<Typography sx={{ ...styles.content }}>
						The measure of percentage of items that were not fulfilled for
						unscheduled orders. This is reported in a percent.
					</Typography>
					<Typography sx={{ ...styles.title }}>
						<li>Calculation:</li>{' '}
					</Typography>
					<Typography sx={{ ...styles.content }}>
						(Unscheduled Nil Pick Rate Numerator) / (Unscheduled Nil Pick Rate
						Denominator).
					</Typography>
				</>
			),
			formula: {
				title: <Typography sx={{ ...styles.title }}>Formula</Typography>,
				content: (
					<Typography sx={{ ...styles.content }}>
						Sum(Quantity of the Items not picked)
					</Typography>
				),
			},
		},
	},
	'total-store-inventory': {
		metric: {
			title: (
				<Typography sx={{ ...styles.title }}>Total Store Inventory:</Typography>
			),
			content: (
				<>
					<Typography sx={{ ...styles.content }}>
						Total Store Inventory is the sum of On Hand, On Order, In Transit
						and In Warehouse Quantities.
					</Typography>
					<Typography sx={{ ...styles.title }}>
						<li>Calculation:</li>
					</Typography>
					<Typography sx={{ ...styles.content }}>
						(Sum Of Total On Hand Inventory and Total On Order Inventory and
						Total In Transit Inventory and Total In WHSE quantity/ No Of days)
					</Typography>
				</>
			),
		},
	},
	'anomaly-details-and-root-cause': {
		metric: {
			content: (
				<>
					<Typography sx={{ ...styles.title }}>Supplier Share chng</Typography>
					<Typography sx={{ ...styles.content }}>
						Basis Point Change in Supplier Share from reference window to
						selected window.
					</Typography>
					<Typography sx={{ ...styles.title }}>Net Sales</Typography>
					<Typography sx={{ ...styles.content }}>
						Total Sales $ in selected window for the supplier
					</Typography>
					<Typography sx={{ ...styles.title }}>Net Sales $ chng</Typography>
					<Typography sx={{ ...styles.content }}>
						Difference in Sales $ between reference window & selected window for
						the supplier
					</Typography>
					<Typography sx={{ ...styles.title }}>Net Sales % chng</Typography>
					<Typography sx={{ ...styles.content }}>
						% change in sales between reference window & selected window for the
						supplier
					</Typography>
				</>
			),
		},
	},
};
